import {makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {ITournament, ITournamentsStore} from "data/stores/tournaments/tournaments.store";
import {includes, isNull} from "lodash";
import type {ITeamStore} from "data/stores/team/team.store";
import {
	getCompletedOrOfficialRoundStatuses,
	getPlayingRoundStatuses,
	getScheduledRoundStatuses,
	getSuspendedRoundStatuses,
} from "data/utils/helper";
import type {IPoolPlayersStore} from "data/stores/pool_players/pool_players.store";
import {TournamentStatus} from "data/enums";

export interface IRoundsBannerController extends ViewController {
	readonly i18n: ILocalizationStore;

	get selectedTournament(): ITournament | undefined;
	get banner(): {
		className: string;
		title: string;
	} | null;
}

@injectable()
export class RoundsBannerController implements IRoundsBannerController {
	get hasTeam() {
		return this._teamStore.hasTeam;
	}

	get selectedTournament() {
		if (!isNull(this._tournamentsStore.selectedTournament)) {
			return this._tournamentsStore.selectedTournament;
		}
	}

	get selectedRound() {
		return this._tournamentsStore.selectedRound;
	}

	get selectedTournamentRounds() {
		return this._tournamentsStore.selectedTournamentRounds;
	}

	get isScheduled() {
		return includes(getScheduledRoundStatuses(), this.selectedRound?.status);
	}

	get isLive() {
		return includes(
			[...getPlayingRoundStatuses(), ...getSuspendedRoundStatuses()],
			this.selectedRound?.status
		);
	}

	get isComplete() {
		return includes(getCompletedOrOfficialRoundStatuses(), this.selectedRound?.status);
	}

	get isCurrentTournamentVisible() {
		return this._tournamentsStore.currentTournament?.id === this.selectedTournament?.id;
	}

	get hasPoolPlayers() {
		return Boolean(this._poolPlayersStore.sortedPlayers.length);
	}

	get isHideBanner() {
		return (
			!this.isCurrentTournamentVisible ||
			this.selectedTournament?.status === TournamentStatus.Completed
		);
	}

	get banner() {
		if (this.isHideBanner) return null;

		if (this.isScheduled && this.hasPoolPlayers) {
			if (this.hasTeam) return null;

			return {
				className: "scheduled",
				title: this.i18n.t(
					"banner.title.scheduled",
					`Pick your <b>“Round {{X}}”</b> roster`,
					{
						X: this.selectedRound?.name,
					}
				),
			};
		}

		if (this.isLive) {
			const nextRound =
				this.selectedTournamentRounds[this._tournamentsStore.selectedRoundIndex + 1];
			const liveTitle = this.i18n.t(
				"banner.title.live_with_next_round",
				` <b>“Round {{X}}”</b> is in progress. Pick your <b>“Round {{Y}}”</b> roster`,
				{
					X: this.selectedRound?.name,
					Y: nextRound?.name,
				}
			);
			const liveTitleWithoutNextRound = this.i18n.t(
				"banner.title.live",
				`Pick your <b>“Round {{X}}”</b> roster`,
				{
					X: this.selectedRound?.name,
				}
			);

			return {
				title: nextRound ? liveTitle : liveTitleWithoutNextRound,
				className: "live",
			};
		}
		// Complete round
		return {
			title: this.i18n.t(
				"banner.title.complete",
				`Select a round above to view your roster and points.`
			),
			className: "complete",
		};
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.TournamentsStore) public _tournamentsStore: ITournamentsStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore,
		@inject(Bindings.PoolPlayersStore) private _poolPlayersStore: IPoolPlayersStore
	) {
		makeAutoObservable(this);
	}
}
